import { App, Flex, Popconfirm } from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { deleteOrderRemove, getPointInventoryGetThunk } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { CheckoutOrder, OrderType, PaymentForm } from "tm-shared/types";
import { buildRoutePath, formatDate, formatUAHStandard } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import {
    useAppDispatch,
    useOfflineModeSelector,
    usePointSelector,
} from "@/hooks/redux";
import { useCheckoutDetailsSelector } from "@/hooks/redux/useProgressSelectors";
import {
    StyledCheckoutSection,
    StyledSectionText,
    StyledSectionTitle,
} from "@/pages/Checkout/components/CheckoutBody/Styled";
import BaseButton from "@/shared/ui/BaseButton";

import { CheckoutCompanyInfo } from "./CheckoutCompanyInfo";
import { CheckoutDetails } from "./CheckoutDetails";
import { CheckoutPaymentInfo } from "./CheckoutPaymentInfo";

interface CheckoutBodyProps {
    checkoutOrder: CheckoutOrder;
    onReset: () => void;
}

const calculatePaymentDate = (
    isPayOnDelivery: boolean,
    effectiveDate: string,
    dayInterval: number | undefined,
): string => {
    if (isPayOnDelivery || !effectiveDate) return effectiveDate;
    return dayjs(effectiveDate, backendDateFormat.date)
        .add(dayInterval || 0, "day")
        .format(backendDateFormat.date);
};

export const CheckoutBody: FC<CheckoutBodyProps> = ({
    checkoutOrder,
    onReset,
}) => {
    const { notification } = App.useApp();

    const {
        orderId,
        pointId,
        marketName,
        address,
        contragent,
        creationDate,
        shipmentDate,
        customShipmentDate,
        paymentForm,
        orderType,
        products,
        closed = false,
        isCarrier: orderIsCarrier,
        isSelfDelivery: orderIsSelfDelivery,
        isCredit,
        comments,
    } = checkoutOrder;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const offlineMode = useOfflineModeSelector();
    const currentDetails = useCheckoutDetailsSelector(orderId);
    const currentPoint = usePointSelector(pointId);

    const isCarrier = closed ? orderIsCarrier : !!currentDetails?.isCarrier;
    const isSelfDelivery = closed
        ? orderIsSelfDelivery
        : !!currentDetails?.isSelfPickup;
    const isPayOnDelivery = closed
        ? !isCredit
        : !!currentDetails?.isPayOnDelivery;
    const payOnDeliveryComment = closed
        ? comments?.length
            ? comments.map((c) => `• ${c}`).join("\n")
            : ""
        : currentDetails?.comment ?? "";

    const paymentInterval =
        paymentForm === PaymentForm.Card1
            ? currentPoint?.context?.paymentF1DayInterval
            : currentPoint?.context?.paymentF2DayInterval;

    const hasCustomDate = customShipmentDate && customShipmentDate !== "-";
    const effectiveDate = hasCustomDate ? customShipmentDate : shipmentDate;

    const paymentDate = formatDate(
        calculatePaymentDate(isPayOnDelivery, effectiveDate, paymentInterval),
        "-",
    );

    const totalSum = useMemo(
        () =>
            Object.values(products).reduce(
                (sum, p) => sum + p.price * p.count,
                0,
            ),
        [products],
    );

    const handleRemoveOrder = useCallback(async () => {
        try {
            await deleteOrderRemove({ orderId });
            notification.success({ title: "Замовлення успішно скасовано" });
            await onReset();
            if (orderType === OrderType.R) {
                await dispatch(getPointInventoryGetThunk({ pointId })).unwrap();
            }
        } catch (error) {
            notification.error({
                title: "Помилка при скасуванні замовлення",
                description: error instanceof Error ? error.message : undefined,
            });
        }
    }, [notification, dispatch, orderId, orderType, pointId, onReset]);

    return (
        <Flex vertical gap={16}>
            <CheckoutCompanyInfo
                companyName={marketName}
                address={address}
                contragent={contragent}
            />

            <CheckoutPaymentInfo
                orderId={orderId}
                orderType={orderType}
                creationDate={creationDate}
                shipmentDate={shipmentDate}
                customShipmentDate={customShipmentDate}
                paymentDate={paymentDate}
                paymentForm={paymentForm}
                closed={closed}
            />

            <CheckoutDetails
                orderId={orderId}
                orderType={orderType}
                paymentForm={paymentForm}
                isCarrier={isCarrier}
                isSelfDelivery={isSelfDelivery}
                isPayOnDelivery={isPayOnDelivery}
                payOnDeliveryComment={payOnDeliveryComment}
                closed={closed}
            />

            <>
                {orderType !== OrderType.R && (
                    <StyledCheckoutSection>
                        <Flex justify="space-between">
                            <StyledSectionTitle>Сума</StyledSectionTitle>
                            <StyledSectionText>
                                {formatUAHStandard(totalSum)}
                            </StyledSectionText>
                        </Flex>
                    </StyledCheckoutSection>
                )}

                <Flex vertical gap={8}>
                    <BaseButton
                        type="primary"
                        onClick={() =>
                            navigate(
                                `${buildRoutePath(ROUTES.CheckoutOrderDetails, {
                                    orderId,
                                })}?isClosed=${closed}`,
                            )
                        }
                        aria-label="Переглянути товари"
                    >
                        Переглянути товари
                    </BaseButton>

                    {!offlineMode && !closed && (
                        <Popconfirm
                            title="Ви впевнені?"
                            description="Скасування замовлення є незворотною дією"
                            onConfirm={handleRemoveOrder}
                            okText="Так"
                            cancelText="Ні"
                        >
                            <BaseButton danger>
                                Скасувати
                                {orderType === OrderType.R
                                    ? " повернення"
                                    : " замовлення"}
                            </BaseButton>
                        </Popconfirm>
                    )}
                </Flex>
            </>
        </Flex>
    );
};
